import * as yup from 'yup';
import { IMetricsDataModel } from '../data-models/metrics.data-model';
import { MetricsFundDataSchema } from './MetricsFundData.schema';
import { MetricsTransactionSchema } from './MetricsTransaction.schema';
import { isoDateStringField } from './isoDateStringField';

/**
 * A deliberately basic schema of the metrics row: the first-level scalar
 * fields, customData (which surfaces on calculated rows), and `transactions`
 * and `fundData` as arrays of records. Other nested and array members
 * (company, cashflows, kpiData, ...) are intentionally omitted. First consumer is the
 * formula-field vocabulary (field existence + type; an array of records is a
 * list a formula can REDUCE over); the `satisfies` clause keeps every key
 * honest against IMetricsDataModel.
 */
export function metricsFields() {
  return {
    acquisitionDate: isoDateStringField(),
    acquisitionShares: yup.number(),
    activeStatus: yup.string(),
    adjustedRealizedGL: yup.number(),
    adjustedRealizedMOIC: yup.number(),
    adjustedUnrealizedGL: yup.number(),
    adjustedUnrealizedMOIC: yup.number(),
    amountInvested: yup.number(),
    boardSentiment: yup.string(),
    budgetCashBalance: yup.number(),
    budgetCashBurn: yup.number(),
    budgetEbitda: yup.number(),
    budgetGrossMargin: yup.number(),
    budgetGrossProfit: yup.number(),
    budgetNetIncome: yup.number(),
    budgetOperatingIncome: yup.number(),
    budgetRevenue: yup.number(),
    budgetTotalDebt: yup.number(),
    budgetWorkingCapital: yup.number(),
    cashBalance: yup.number(),
    cashBurn: yup.number(),
    cashBurnRate: yup.number(),
    cashDividendReceived: yup.number(),
    cashInterestReceived: yup.number(),
    cashRunway: yup.string(),
    companyId: yup.number(),
    conversionNoOfShares: yup.number(),
    convertedFDS: yup.number(),
    convertedOwnership: yup.number(),
    convertedSharesHeld: yup.number(),
    currentCost: yup.number(),
    customData: yup.object(),
    cy2CashBalance: yup.number(),
    cy2CashBurn: yup.number(),
    cy2Ebitda: yup.number(),
    cy2GrossProfit: yup.number(),
    cy2NetIncome: yup.number(),
    cy2OperatingIncome: yup.number(),
    cy2Revenue: yup.number(),
    cy2TotalDebt: yup.number(),
    cy2WorkingCapital: yup.number(),
    cyCashBalance: yup.number(),
    cyCashBurn: yup.number(),
    cyEbitda: yup.number(),
    cyGrossProfit: yup.number(),
    cyNetIncome: yup.number(),
    cyOperatingIncome: yup.number(),
    cyRevenue: yup.number(),
    cyTotalDebt: yup.number(),
    cyWorkingCapital: yup.number(),
    daysSinceLastValuationChange: yup.number(),
    dilutionAssumption: yup.number(),
    distributions: yup.number(),
    dividends: yup.number(),
    dpi: yup.number(),
    ebitda: yup.number(),
    entryOwnership: yup.number(),
    equityFmv: yup.number(),
    escrowAmount: yup.number(),
    exitDate: isoDateStringField(),
    exitOwnershipPercentage: yup.number(),
    exitType: yup.string(),
    exitValuation: yup.number(),
    expenses: yup.number(),
    finalExitDate: isoDateStringField(),
    fmv: yup.number(),
    followOnInvestment: yup.number(),
    fullTimeEmployees: yup.number(),
    fund: yup.string(),
    fundData: yup.array().of(yup.object(MetricsFundDataSchema.base())),
    grossProfit: yup.number(),
    hasPendingTransactionImpact: yup.boolean(),
    impliedEquityValue: yup.number(),
    impliedOwnershipPercentage: yup.number(),
    initialInvestment: yup.number(),
    initialInvestmentDate: isoDateStringField(),
    initialInvestmentRound: yup.string(),
    initialInvestmentStage: yup.string(),
    initialPostMoney: yup.number(),
    initialPreMoney: yup.number(),
    initialSharePrice: yup.number(),
    initialSharesHeld: yup.number(),
    initialSharesReceiptDate: isoDateStringField(),
    interest: yup.number(),
    investmentAge: yup.number(),
    investmentStatus: yup.string(),
    irr: yup.number(),
    kpiLastReportedDateActual: isoDateStringField(),
    kpiLastReportedDateBudget: isoDateStringField(),
    lastExitDate: isoDateStringField(),
    lastPostMoneyRound: yup.number(),
    lastValuationChangeDate: isoDateStringField(),
    ltmCashBalance: yup.number(),
    ltmCashBurn: yup.number(),
    ltmEbitda: yup.number(),
    ltmGrossProfit: yup.number(),
    ltmNetIncome: yup.number(),
    ltmOperatingIncome: yup.number(),
    ltmRevenue: yup.number(),
    ltmTotalDebt: yup.number(),
    ltmWorkingCapital: yup.number(),
    moic: yup.number(),
    moicValueCost: yup.number(),
    monthlyRevenueGrowth: yup.number(),
    monthlygrossMargin: yup.number(),
    navPercentage: yup.number(),
    netIncome: yup.number(),
    netMoic: yup.number(),
    netTotalValue: yup.number(),
    noOfShares: yup.number(),
    normalizedMostRecentStage: yup.string(),
    operatingIncome: yup.number(),
    ownerShipPercentage: yup.number(),
    participatingLastRoundSeries: yup.string(),
    participatingLastRoundSharePrice: yup.number(),
    participatingLastRoundSharePriceDate: isoDateStringField(),
    participatingMostRecentFinancingAmount: yup.number(),
    percentOfFmv: yup.number(),
    percentOfFund: yup.number(),
    percentTotalValue: yup.number(),
    pricePerShare: yup.number(),
    projectedMOIC: yup.number(),
    projectedProceeds: yup.number(),
    realizedAmountWithEscrow: yup.number(),
    realizedCost: yup.number(),
    realizedGL: yup.number(),
    fxRealizedGL: yup.number().nullable(),
    fxUnrealizedGL: yup.number().nullable(),
    investmentRealizedGL: yup.number().nullable(),
    investmentUnrealizedGL: yup.number().nullable(),
    realizedGLCost: yup.number(),
    realizedGLCostBasis: yup.number(),
    realizedGLWithEscrow: yup.number(),
    realizedGLWithEscrowCost: yup.number(),
    realizedMOIC: yup.number(),
    realizedMOICCost: yup.number(),
    realizedMOICCostBasis: yup.number(),
    realizedMOICWithEscrow: yup.number(),
    realizedMOICWithEscrowCost: yup.number(),
    realizedValue: yup.number(),
    realizedValueCostBasis: yup.number(),
    recyclableCapital: yup.number(),
    reserves: yup.number(),
    rtfe: yup.number(),
    rvpi: yup.number(),
    seedCore: yup.string(),
    stockProceeds: yup.number(),
    totalCost: yup.number(),
    totalDebt: yup.number(),
    totalGL: yup.number(),
    totalGLWithEscrow: yup.number(),
    totalGLWithEscrowCost: yup.number(),
    totalGlCost: yup.number(),
    totalImpliedEquityValue: yup.number(),
    totalImpliedOwnershipEquityValue: yup.number(),
    totalReturn: yup.number(),
    totalRevenue: yup.number(),
    totalValue: yup.number(),
    transactions: yup.array().of(yup.object(MetricsTransactionSchema.base())),
    tvpi: yup.number(),
    unrealizedCost: yup.number(),
    unrealizedGL: yup.number(),
    unrealizedGLCost: yup.number(),
    unrealizedGLCostBasis: yup.number(),
    unrealizedMOIC: yup.number(),
    unrealizedMOICCost: yup.number(),
    unrealizedPps: yup.number(),
    unrealizedValue: yup.number(),
    unrealizedValueCostBasis: yup.number(),
    unrealizedValueWithInterest: yup.number(),
    workingCapital: yup.number(),
    yearlyRevenueGrowth: yup.number(),
    yearlygrossMargin: yup.number(),
    yearsToFinalExit: yup.number(),
    yearsToInitialExit: yup.number(),
  } satisfies Partial<Record<keyof IMetricsDataModel | 'customData', yup.ISchema<unknown>>>;
}

export function metricsSchema() {
  return yup.object(metricsFields());
}
